import { logger } from '../utils/logger';

// Note: Using clerkApiClient for authenticated requests to bitemporal endpoints

// Lazy import to avoid circular dependency
const getApiClient = async () => {
  const { clerkApiClient } = await import('./clerk-api-client');
  return clerkApiClient;
};

// Bitemporal Type Definitions
export interface BitemporalMetadata {
  entity_id: string;
  record_id: string;
  version: number;
  transaction_time_start: string;
  transaction_time_end?: string;
  valid_time_start: string;
  valid_time_end?: string;
  created_by?: string;
  updated_by?: string;
  is_current: boolean;
}

export interface BitemporalResponse<T> {
  data: T;
  bitemporal_metadata: BitemporalMetadata;
  query_time: string;
}

export interface VersionHistoryResponse<T> {
  entity_id: string;
  total_versions: number;
  versions: BitemporalResponse<T>[];
}

// Import all entity types from the main API service
import type {
  Credit,
} from './api';

// Entity type mapping for API endpoints
export const ENTITY_TYPE_MAPPINGS = {
  credits: 'credits',
  users: 'users',
  instructors: 'instructors',
  locations: 'locations',
  rooms: 'rooms',
  'class-categories': 'class-categories',
  classes: 'classes',
  'credit-packages': 'credit-packages',
  memberships: 'memberships',
  bookings: 'bookings',
  'store-items': 'store-items',
  'promo-codes': 'promo-codes',
  'class-templates': 'class-templates',
  admins: 'admins'
} as const;

export type EntityType = keyof typeof ENTITY_TYPE_MAPPINGS;

// Generic entity interface for bitemporal operations
export interface BitemporalEntity {
  id: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

// Bitemporal API Functions

// ==================== CREDIT BITEMPORAL OPERATIONS ====================

export const getCreditHistory = async (entityId: string): Promise<VersionHistoryResponse<Credit>> => {
  const client = await getApiClient();
  const response = await client.get<VersionHistoryResponse<Credit>>(`/bitemporal/credits/${entityId}/history`);
  return response;
};

export const getCreditAtTime = async (entityId: string, pitTime: string): Promise<BitemporalResponse<Credit>> => {
  const client = await getApiClient();
  const response = await client.get<BitemporalResponse<Credit>>(`/bitemporal/credits/${entityId}/at-time?pit_time=${encodeURIComponent(pitTime)}`);
  return response;
};

export const getAllCreditsAtTime = async (pitTime: string): Promise<BitemporalResponse<Credit>[]> => {
  const client = await getApiClient();
  const response = await client.get<BitemporalResponse<Credit>[]>(`/bitemporal/credits/all/at-time?pit_time=${encodeURIComponent(pitTime)}`);
  return response;
};

export const createCreditBitemporal = async (credit: Omit<Credit, 'id' | 'created_at'>, createdBy?: string): Promise<Credit> => {
  const client = await getApiClient();
  const url = `/bitemporal/credits${createdBy ? `?created_by=${encodeURIComponent(createdBy)}` : ''}`;
  const response = await client.post<Credit>(url, credit);
  return response;
};

export const updateCreditBitemporal = async (creditId: string, credit: Partial<Credit>, updatedBy?: string): Promise<Credit> => {
  const client = await getApiClient();
  const url = `/bitemporal/credits/${creditId}${updatedBy ? `?updated_by=${encodeURIComponent(updatedBy)}` : ''}`;
  const response = await client.put<Credit>(url, credit);
  return response;
};

export const deleteCreditBitemporal = async (creditId: string, deletedBy?: string): Promise<void> => {
  const client = await getApiClient();
  const url = `/bitemporal/credits/${creditId}${deletedBy ? `?deleted_by=${encodeURIComponent(deletedBy)}` : ''}`;
  await client.delete(url);
};

// ==================== GENERIC BITEMPORAL OPERATIONS ====================

// Generic deactivation - sets valid_time_end to current timestamp
export const deactivateEntity = async (
  entityType: EntityType, 
  entityId: string, 
  deactivatedBy?: string
): Promise<void> => {
  const client = await getApiClient();
  const url = `/bitemporal/${entityType}/${entityId}/deactivate${deactivatedBy ? `?deactivated_by=${encodeURIComponent(deactivatedBy)}` : ''}`;
  await client.put(url, {});
};

// Generic reactivation - creates new record with valid_time_start = now
export const reactivateEntity = async <T extends BitemporalEntity>(
  entityType: EntityType, 
  entityId: string, 
  reactivatedBy?: string
): Promise<T> => {
  const client = await getApiClient();
  const url = `/bitemporal/${entityType}/${entityId}/reactivate${reactivatedBy ? `?reactivated_by=${encodeURIComponent(reactivatedBy)}` : ''}`;
  const response = await client.post<T>(url, {});
  return response;
};

// Generic bitemporal update - creates new version with changes
export const updateEntityBitemporal = async <T extends BitemporalEntity>(
  entityType: EntityType, 
  entityId: string, 
  updates: Partial<T>, 
  updatedBy?: string,
  reason?: string
): Promise<T> => {
  const client = await getApiClient();
  const url = `/bitemporal/${entityType}/${entityId}/modify${updatedBy ? `?updated_by=${encodeURIComponent(updatedBy)}` : ''}`;
  const requestBody = {
    data: updates,
    ...(reason && { reason })
  };
  const response = await client.post<T>(url, requestBody);
  return response;
};

// Generic history retrieval
export const getEntityHistory = async <T extends BitemporalEntity>(
  entityType: EntityType, 
  entityId: string
): Promise<VersionHistoryResponse<T>> => {
  const client = await getApiClient();
  const url = `/bitemporal/${entityType}/${entityId}/history`;
  logger.debug(`Bitemporal API requesting history: ${client.getBaseUrl()}${url}`);
  
  try {
    const response = await client.get<VersionHistoryResponse<T>>(url);
    return response;
  } catch (error: any) {
    logger.error(`Bitemporal API history request failed for ${entityType}/${entityId}`, {
      url: `${client.getBaseUrl()}${url}`,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });
    throw error;
  }
};

// Generic point-in-time retrieval
export const getEntityAtTime = async <T extends BitemporalEntity>(
  entityType: EntityType, 
  entityId: string, 
  pitTime: string
): Promise<BitemporalResponse<T>> => {
  // Use clerk API client for authentication, but construct the URL manually since it doesn't support params
  const client = await getApiClient();
  const url = `/bitemporal/${entityType}/${entityId}/at-time?pit_time=${encodeURIComponent(pitTime)}`;
  const response = await client.get<BitemporalResponse<T>>(url);
  return response;
};

// Generic create with bitemporal metadata
export const getAllEntitiesBitemporal = async <T extends BitemporalEntity>(
  entityType: EntityType,
  includeInactive = false
): Promise<T[]> => {
  const client = await getApiClient();
  const url = `/bitemporal/${entityType}${includeInactive ? '?include_inactive=true' : ''}`;
  const response = await client.get<T[]>(url);
  return response;
};

export const createEntityBitemporal = async <T extends BitemporalEntity>(
  entityType: EntityType, 
  entityData: Omit<T, 'id' | 'created_at' | 'updated_at'>, 
  createdBy?: string
): Promise<T> => {
  const client = await getApiClient();
  const url = `/bitemporal/${entityType}${createdBy ? `?created_by=${encodeURIComponent(createdBy)}` : ''}`;
  const response = await client.post<T>(url, entityData);
  return response;
};

// ==================== UTILITY FUNCTIONS ====================

export const formatDateTime = (dateTimeString: string): string => {
  return new Date(dateTimeString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  });
};

export const formatDateTimeForInput = (dateTimeString: string): string => {
  // Format for datetime-local input (YYYY-MM-DDTHH:MM)
  return new Date(dateTimeString).toISOString().slice(0, 16);
};

export const getCurrentDateTime = (): string => {
  return new Date().toISOString();
};

// Export a function that gets the client to avoid circular dependency
export default getApiClient;